import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';

/**
 * NavSidebar — 左栏导航菜单（站点全量导航 + S0-S4 权限过滤）
 *
 * 权限规则（任务 H）：
 * - 管理员（user.isAdmin()）全部可见
 * - 了解塔罗（原课程学习）：仅 S0 可见
 * - 课程中心/考试/导师：S1+
 * - 案例板块等级递进：S1 案例 N=1，S2 N=2，S3 N=3，S4 N=4（levelNum >= N 可见）
 * - 客服工单/首页/我的关注/标签/设置：所有等级
 * - 七星查询：S3+
 * - 我的提问/积分兑换/我的案例：S1+
 * - 发布案例按钮：S1+；发起工单按钮：所有等级
 */
export default class NavSidebar extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.currentPath = (m.route.get && m.route.get()) || '/';

    // ---------- 等级 / 管理员计算 ----------
    const user = app.session.user;
    this.isAdmin = !!(user && typeof user.isAdmin === 'function' && user.isAdmin());
    const rawLevel = user && user.attribute ? user.attribute('taroSwordLevel') : null;
    this.userLevel = rawLevel || 'S0';
    const m2 = /^S(\d)$/.exec(this.userLevel);
    this.levelNum = m2 ? parseInt(m2[1], 10) : 0;
  }

  /** 导航项权限过滤（admin 恒 true） */
  canShow(rule) {
    if (this.isAdmin) return true;
    if (rule === 'any') return true;
    if (rule === 's0only') return this.userLevel === 'S0';
    if (rule === 's1plus') return this.levelNum >= 1;
    if (rule === 's3plus') return this.levelNum >= 3;
    if (typeof rule === 'number') return this.levelNum >= rule; // 案例板块：levelNum >= N
    return true;
  }

  /** 权限过滤 + 渲染 menuItem；不可见时返回 null */
  menuRule(icon, label, href, opts, rule) {
    if (!this.canShow(rule)) return null;
    return this.menuItem(icon, label, href, opts || {});
  }

  view() {
    const user = app.session.user;

    return (
      <nav className="TaroNav" oncreate={() => this._bindNavClicks()}>
        <div className="TaroNav-user">
          {user ? (
            <a href={app.route.user(user)} className="TaroNav-userLink">
              <img className="TaroNav-userAvatar" src={user.avatarUrl()} alt={user.displayName()} />
              <span className="TaroNav-userName">{user.displayName()}</span>
            </a>
          ) : (
            <button type="button" className="Button Button--primary TaroNav-loginBtn" onclick={() => app.showLogInModal()}>
              登录 / 注册
            </button>
          )}
        </div>

        <div className="TaroNav-scroll">
          <ul className="TaroNav-menu">
            {this.menuRule('🏠', '首页', '/', { exact: true }, 'any')}
            {this.menuRule('🎓', '了解塔罗', '/courses', null, 's0only')}
            {this.menuRule('📚', '课程中心', '/course-library', null, 's1plus')}
            {this.menuRule('📝', '考试中心', '/exams', null, 's1plus')}
            {this.menuRule('🧑‍🏫', '导师中心', '/mentor-center', null, 's1plus')}
            {this.menuRule('👀', '我的关注', user ? '/following' : null, { soon: !user }, 'any')}
            {this.menuRule('🏷️', '标签', '/tags', null, 'any')}

            {(this.isAdmin || this.levelNum >= 1) ? <li className="TaroNav-divider" /> : null}
            {this.menuRule('🌱', 'S1 案例', '/t/s1-case', null, 1)}
            {this.menuRule('🌟', 'S2 案例', '/t/s2-cases', null, 2)}
            {this.menuRule('⭐', 'S3 案例', '/t/s3-case', null, 3)}
            {this.menuRule('👑', 'S4 案例', '/t/s4-case', null, 4)}
            {this.menuRule('🎫', '客服工单', '/t/ticket', null, 'any')}

            {this.canShow('s3plus') ? <li className="TaroNav-divider" /> : null}
            {this.canShow('s3plus') ? (
              <li className="TaroNav-item">
                <a
                  className="TaroNav-link"
                  href="#"
                  onclick={(e) => {
                    e.preventDefault();
                    if (!app.session.user) { app.showLogInModal(); return; }
                    m.route.set('/sevenstar');
                    document.dispatchEvent(new CustomEvent('taroshell:nav-close'));
                  }}
                >
                  <span className="TaroNav-icon">✴️</span>
                  <span className="TaroNav-label">七星查询</span>
                </a>
              </li>
            ) : null}
            {this.menuRule('❓', '我的提问', user ? '/my-questions' : null, { soon: !user }, 's1plus')}
            {this.menuRule('💰', '积分兑换', user ? '/u/' + user.slug() + '/credits' : null, { soon: !user }, 's1plus')}
            {this.menuRule('✍️', '我的案例', user ? app.route('user.taroCases', { username: user.slug() }) : null, { soon: !user }, 's1plus')}
            {this.menuRule('⚙️', '设置', user ? '/settings' : null, { soon: !user }, 'any')}
          </ul>
        </div>

        {user ? (
          <div className="TaroNav-bottomBtns">
            {this.canShow('s1plus') && (
              <button
                type="button"
                className="Button Button--primary TaroNav-publishBtn"
                onclick={() => {
                  document.dispatchEvent(new CustomEvent('taroshell:nav-close'));
                  m.route.set('/publish-case');
                }}
              >
                <i className="fas fa-plus" />
                <span>发布案例</span>
              </button>
            )}
            <button
              type="button"
              className="Button TaroNav-ticketBtn"
              onclick={() => {
                const ticketTag = app.store.all('tags').find((t) => t.slug() === 'ticket');
                app.composer.show(DiscussionComposer, { tags: ticketTag ? [ticketTag] : [] });
                document.dispatchEvent(new CustomEvent('taroshell:nav-close'));
              }}
            >
              <i className="fas fa-ticket-alt" />
              <span>发起工单</span>
            </button>
          </div>
        ) : null}

        {this.userLevel === 'S0' && !this.isAdmin ? (
          <div className="TaroNav-upgradeHint">
            <i className="fas fa-arrow-up" /> 升级到 S1 解锁课程、案例、导师等全部功能
          </div>
        ) : null}
      </nav>
    );
  }

  menuItem(icon, label, href, opts = {}) {
    const isSoon = opts.soon || !href;
    const active =
      !isSoon && opts.exact
        ? this.currentPath === href
        : !isSoon && href && href !== '/' && this.currentPath.indexOf(href) === 0;

    return (
      <li className={'TaroNav-item' + (active ? ' is-active' : '') + (isSoon ? ' is-soon' : '')}>
        {isSoon ? (
          <span className="TaroNav-link" title="即将上线">
            <span className="TaroNav-icon">{icon}</span>
            <span className="TaroNav-label">{label}</span>
            <span className="TaroNav-soonBadge">soon</span>
          </span>
        ) : (
          <a className="TaroNav-link" href={href}>
            <span className="TaroNav-icon">{icon}</span>
            <span className="TaroNav-label">{label}</span>
          </a>
        )}
      </li>
    );
  }

  /** SPA 内部跳转（拦截同源链接，交给 mithril router） */
  _bindNavClicks() {
    const nav = this.element;
    if (!nav) return;
    nav.addEventListener('click', (e) => {
      // 元素自身的 onclick 已 preventDefault（如弹窗类入口）则不导航
      if (e.defaultPrevented) return;
      const link = e.target.closest && e.target.closest('a.TaroNav-link[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href === '#' || href.startsWith('http')) return; // 外链/占位放行

      e.preventDefault();
      this.currentPath = href;
      m.route.set(href);
      document.dispatchEvent(new CustomEvent('taroshell:nav-close')); // 手机端关闭抽屉
    });
  }
}